/**
 * Role-based dashboard panels for CaseMinder.
 * Each panel is a self-contained component that fetches its own data.
 *
 * FIX (Apr 2026): ALL panels now pass X-Bonsai-User-Id header on every
 * request so the backend can scope data to the logged-in user.
 *
 * FIX (12 Apr 2026 — P3.1): DashboardHome now calls GET /api/staff/me
 * on mount to retrieve dashboard_panels for the current user.
 * Panel visibility is driven entirely by staff_config.DASHBOARD_PANELS
 * on the backend — no more hardcoded role checks in JSX.
 * Directors Lounge is NOT rendered on the homepage; it is accessible
 * via the nav bar only (role-gated to DIRECTOR).
 *
 * FIX (13 Apr 2026): MyMattersPanel — directors in "mine" mode now route
 * to /submissions with fee_earner filter instead of /hub/dashboard.
 * Fixes spinner/hang for directors whose feInfo resolves correctly.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../config/api";
import { Loader2, Clock, CheckCircle, ChevronRight, FileText, Scale, Inbox, Calendar, Users, ClipboardCheck, Flag, ToggleLeft, ToggleRight, PoundSterling, Receipt, CreditCard, Banknote, FileSpreadsheet, Plus, Upload, X as XIcon, Lock, RefreshCw } from "lucide-react";
import { getUserFeeEarner, getUserRole, ROLES, SUPERVISED_FEE_EARNERS } from "../config/roleConfig";
import { toast } from "sonner";
import { errorMessage } from "../utils/errorMessage";
import { format, parseISO, differenceInDays } from "date-fns";

const userId = () => sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id");
const userEmail = () => (
  sessionStorage.getItem("bonsai_user_email") ||
  localStorage.getItem("bonsai_user_email") ||
  sessionStorage.getItem("bonsai_user_id") ||
  ""
).toLowerCase();

const headers = () => ({
  "X-Bonsai-User-Id": userId(),
  "X-Bonsai-User-Email": userEmail(),
});

const pick = (...vals) => {
  for (const v of vals) {
    if (v != null && v !== "") return v;
  }
  return "";
};

const initials = (name) => {
  if (!name) return "";
  return name.split(/\s+/).map(w => w[0]).join("").toUpperCase();
};

function PanelShell({ title, icon: Icon, color, loading, error, children, testId }) {
  return (
    <div className="bg-navy-card border border-navy-border rounded-lg overflow-hidden" data-testid={testId}>
      <div className={`px-4 py-3 border-b border-navy-border flex items-center gap-2`}>
        {Icon && <Icon className={`w-4 h-4 ${color || "text-teal"}`} />}
        <h3 className="text-sm font-semibold text-white">{title}</h3>
      </div>
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-teal" /></div>
        ) : error ? (
          <div className="text-center py-6 text-status-red text-xs">{error}</div>
        ) : children}
      </div>
    </div>
  );
}

function StatusBadge({ status, label }) {
  const colors = {
    clear: "bg-teal-dim/40 text-teal",
    pending: "bg-status-amber-dim/40 text-status-amber",
    blocked: "bg-status-red-dim/40 text-status-red",
    overdue: "bg-status-red-dim/40 text-status-red",
    at_risk: "bg-status-amber-dim/40 text-status-amber",
    done: "bg-teal-dim/40 text-teal",
    stale: "bg-slate-700/40 text-slate-400",
  };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${colors[status] || colors.pending}`}>{label || status}</span>;
}

function TableRow({ children, onClick }) {
  return (
    <tr className={`border-b border-navy-border text-xs text-txt-muted ${onClick ? "cursor-pointer hover:bg-navy-hover/50" : ""}`} onClick={onClick}>
      {children}
    </tr>
  );
}

// ═══════════════════════════════════════════════
// DashboardHome — top-level container
// Calls /api/staff/me, renders panels from dashboard_panels list.
// ═══════════════════════════════════════════════
export function DashboardHome() {
  const [staffRecord, setStaffRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const email = userEmail();

  useEffect(() => {
    axios.get(`${API}/staff/me`, { headers: headers() })
      .then(res => setStaffRecord(res.data))
      .catch(() => {
        // Fallback: universal panels only
        setStaffRecord({
          email,
          role: "UNKNOWN",
          dashboard_panels: ["MY_MATTERS", "TODAY", "BONSAI_MATE"],
        });
      })
      .finally(() => setLoading(false));
  }, [email]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-6 h-6 animate-spin text-teal" />
      </div>
    );
  }

  const panels = staffRecord?.dashboard_panels || [];
  const show = (key) => panels.includes(key);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
      {/* Universal panels — everyone */}
      {show("MY_MATTERS") && <MyMattersPanel userEmail={email} />}
      {show("TODAY") && <DeadlinesNudgesPanel userEmail={email} />}

      {/* Resi pipeline panels */}
      {show("RESI_CHECKLISTS") && <ResiChecklistsPanel userEmail={email} />}
      {show("POST_COMPLETION") && <PostCompletionTasksPanel userEmail={email} />}

      {/* Golden Rule / Client Care panels */}
      {show("GOLDEN_RULE_PIPELINE") && <GoldenRulePipelinePanel />}
      {show("FILE_CLOSURE_RISK") && <FileClosureRiskPanel userEmail={email} />}

      {/* Accounts panel */}
      {show("ACCOUNTS_TODAY") && <AccountsTodayPanel userEmail={email} />}

      {/* NOTE: Directors Lounge is intentionally NOT rendered here.
          It is accessible via the nav bar only, gated to DIRECTOR role. */}
    </div>
  );
}

// BM-H553 (16 Sep 2026): one vocabulary for "the care pack has gone out".
// Three routes send it and each records it differently: the portal send writes
// care_pack_sent on the submission, the off-system route writes
// ccl_pack_sent_off_system_at on the onboarding record, and the manual-sign and
// client-confirmation routes write a signature and nothing else. Reading only
// the first put "Care pack not sent" beside matters the client had already
// signed. Mirrors hub.pipeline.care_pack_sent_recorded, which the file-open
// gate itself asks.
function carePackSentRecorded(sub) {
  const d = (sub && sub.data) || {};
  return Boolean(
    sub?.care_pack_sent || sub?.care_pack_sent_at ||
    d.care_pack_sent || d.care_pack_sent_at ||
    sub?.ccl_pack_sent_off_system_at || d.ccl_pack_sent_off_system_at ||
    sub?.ccl_signed_at || d.ccl_signed_at ||
    sub?.status === "ccl_signed" || d.status === "ccl_signed"
  );
}


// ═══════════════════════════════════════════════
// 2.1 Golden Rule Pipeline Panel (Admin)
// ═══════════════════════════════════════════════
export function GoldenRulePipelinePanel() {
  const navigate = useNavigate();
  const [data, setData] = useState({ submissions: [], conflicts: [], onboarding: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const [subsRes, conflictsRes, onbRes] = await Promise.all([
          axios.get(`${API}/submissions`, { params: { limit: 200 }, headers: headers() }).catch(() => ({ data: { submissions: [] } })),
          axios.get(`${API}/conflict-checks/recent`, { params: { status: "pending_approval", limit: 50 }, headers: headers() }).catch(() => ({ data: [] })),
          axios.get(`${API}/onboarding/list`, { headers: headers() }).catch(() => ({ data: [] })),
        ]);
        const subs = (subsRes.data?.submissions || subsRes.data || []).filter(s => !["archived", "deleted"].includes(s.status));
        setData({
          submissions: subs,
          conflicts: Array.isArray(conflictsRes.data) ? conflictsRes.data : conflictsRes.data?.checks || [],
          onboarding: Array.isArray(onbRes.data) ? onbRes.data : onbRes.data?.records || [],
        });
      } catch (e) {
        setError("Failed to load pipeline data");
      }
      setLoading(false);
    })();
  }, []);

  const getStage = (sub) => {
    if (!sub.conflict_check_id && sub.status === "submitted") return { label: "Conflict check", blocker: "Awaiting conflict check" };
    if (sub.ccl_status !== "reviewed" && sub.ccl_status !== "sent") return { label: "CCL review", blocker: "CCL not reviewed" };
    if (!carePackSentRecorded(sub)) return { label: "Care pack", blocker: "Care pack not sent" };
    return { label: "Complete", blocker: null };
  };

  const rows = data.submissions.map(sub => {
    const stage = getStage(sub);
    const d = sub.data || {};
    const created = sub.created_at || sub.createdAt;
    const age = created ? differenceInDays(new Date(), parseISO(created)) : 0;
    return {
      id: sub.id || sub._id,
      client: pick(sub.client_display_name, d.client_name, d.Q1, sub.primary_label),
      feeEarner: pick(d.fee_earner, d.Q6, d.assigned_to),
      stage: stage.label,
      blocker: stage.blocker,
      age,
      status: sub.status,
      ref: pick(sub.matter_reference, d.matter_reference, sub.provisional_id),
    };
  });

  const filtered = rows.filter(r => {
    if (filter === "director") return data.conflicts.some(c => c.submission_id === r.id);
    if (filter === "client") return data.onboarding.some(o => (o.provisional_id === r.id || o.submission_id === r.id) && o.status === "sent");
    if (filter === "fee_earner") return r.blocker && !r.blocker.includes("conflict") && !r.blocker.includes("Care pack");
    return true;
  });

  return (
    <PanelShell title="Golden Rule Pipeline" icon={ClipboardCheck} color="text-status-amber" loading={loading} error={error} testId="golden-rule-panel">
      <div className="flex gap-1.5 mb-3 flex-wrap">
        {[["all","All"],["director","Needs director"],["client","Waiting on client"],["fee_earner","Waiting on FE"]].map(([v,l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`text-[10px] px-2 py-1 rounded transition-colors ${filter === v ? "bg-status-amber text-white" : "bg-navy-hover text-txt-muted hover:bg-navy-hover"}`}
            data-testid={`pipeline-filter-${v}`}>{l}</button>
        ))}
      </div>
      {filtered.length === 0 ? <p className="text-xs text-txt-muted text-center py-4">No items in pipeline</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="text-[10px] text-txt-muted uppercase tracking-wider border-b border-navy-border">
              <th className="py-2 pr-2">Client</th><th className="py-2 pr-2">FE</th><th className="py-2 pr-2">Stage</th><th className="py-2 pr-2">Days</th><th className="py-2">Blocker</th>
            </tr></thead>
            <tbody>
              {filtered.slice(0, 50).map(r => (
                <TableRow key={r.id} onClick={() => navigate(`/submission/${r.id}`)}>
                  <td className="py-2 pr-2 text-white font-medium">{r.client}</td>
                  <td className="py-2 pr-2">{r.feeEarner}</td>
                  <td className="py-2 pr-2"><StatusBadge status={r.stage === "Complete" ? "done" : "pending"} label={r.stage} /></td>
                  <td className="py-2 pr-2">{r.age}d</td>
                  <td className="py-2 text-status-amber">{r.blocker || "—"}</td>
                  <td className="py-2 pl-1"><ChevronRight className="w-3 h-3 text-txt-muted" /></td>
                </TableRow>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] text-txt-muted mt-2">{filtered.length} matter{filtered.length !== 1 ? "s" : ""} in pipeline</p>
    </PanelShell>
  );
}

// ═══════════════════════════════════════════════
// 2.2 Fee Earner Matters Panel ("My Matters")
// ═══════════════════════════════════════════════
function getMatterStageLabel(sub) {
  const d = sub.data || {};
  const status = sub.status;
  if (status === "draft") return { label: "Intake Draft", badge: "pending" };
  if (status === "open") return { label: "Active Matter", badge: "done" };
  if (status === "care_pack_prepared") return { label: "Care Pack Ready", badge: "pending" };
  if (status === "reviewed") return { label: "Under Review", badge: "pending" };
  if (status === "submitted") {
    if (carePackSentRecorded(sub)) return { label: "Active Matter", badge: "done" };
    if (d.ob_sent || sub.ob_sent) return { label: "Care Pack", badge: "pending" };
    const conflictId = sub.conflict_check_id || d.conflict_check_id;
    if (!conflictId) return { label: "Awaiting Conflict Check", badge: "blocked" };
    return { label: "OB Stage", badge: "pending" };
  }
  return { label: status, badge: "pending" };
}

function matchesFeeEarner(sub, feInfo) {
  if (!feInfo) return false;
  const d = sub.data || {};
  const fe = pick(d.fee_earner, d.Q6, d.assigned_to);
  if (!fe) return false;
  const feUpper = fe.toUpperCase();
  const feInitials = (feInfo.initials || "").toUpperCase();
  const nameFragments = feInfo.nameFragments || [];
  if (nameFragments.some(f => f && fe.includes(f))) return true;
  if (feUpper === feInitials) return true;
  if (fe.toLowerCase().includes(feInfo.name.split(" ")[0].toLowerCase())) return true;
  if (d.assigned_to && d.assigned_to.toLowerCase().includes(feInfo.name.split(" ")[0].toLowerCase())) return true;
  return false;
}

function isResidentialMatter(sub) {
  const d = sub.data || {};
  const ref = (sub.matter_reference || d.matter_reference || "").toUpperCase();
  const paCode = (d.practice_area_code || d.practice_area || "").toUpperCase();
  const mType = (d.matter_type || d.type_of_matter || "").toLowerCase();
  if (paCode === "RES" || paCode === "RESI") return true;
  if (/\bRES\b/.test(ref)) return true;
  if (/resi|purchase|sale|remortgage/.test(mType)) return true;
  return false;
}

export function MyMattersPanel({ practiceAreas, userEmail: userEmailProp, isAssistant, canViewAllMatters }) {
  const navigate = useNavigate();
  const [allMatters, setAllMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [compact, setCompact] = useState(false);
  const [viewMode, setViewMode] = useState("mine");
  const [showWip, setShowWip] = useState(false);
  const [wipData, setWipData] = useState({});
  const DEFAULT_ROWS = 8;

  const feInfo = getUserFeeEarner(userEmailProp);

  useEffect(() => {
    (async () => {
      try {
        if (isAssistant || (canViewAllMatters && viewMode === "all")) {
          const params = { limit: 200 };
          if (practiceAreas?.length > 0) params.practice_areas = practiceAreas.join(",");
          const res = await axios.get(`${API}/submissions`, { params, headers: headers() });
          let subs = res.data?.submissions || res.data || [];
          subs = subs.filter(s => !["archived", "deleted", "closed"].includes(s.status));
          setAllMatters(subs);
        } else {
          // FIX (13 Apr 2026): directors in "mine" mode must NOT hit /hub/dashboard
          // (which waits for my_open_matters and hangs). Route to /submissions
          // with a fee_earner filter instead — same data, faster, consistent.
          const feParams = feInfo
            ? { limit: 200, fee_earner: feInfo.initials }
            : { limit: 200 };
          const res = await axios.get(`${API}/submissions`, { params: feParams, headers: headers() });
          const matters = res.data?.submissions || res.data || [];
          setAllMatters(matters.filter(s => !["archived", "deleted", "closed"].includes(s.status)));
        }
      } catch {
        setError("Failed to load matters");
      }
      setLoading(false);
    })();
  }, [practiceAreas, userEmailProp, isAssistant, viewMode]);

  const matters = (() => {
    if (isAssistant || (canViewAllMatters && viewMode === "all")) {
      if (viewMode === "all") return allMatters;
      const supervisedInitials = (typeof SUPERVISED_FEE_EARNERS !== 'undefined' && SUPERVISED_FEE_EARNERS[userEmailProp]) || (feInfo ? [feInfo.initials] : []);
      const feInfoMap = {
        'VEC': { initials: 'VEC', name: 'Vanessa Challess', nameFragments: ['vanessa', 'challess', 'vec'] },
        'RM': { initials: 'RM', name: 'Rebekah Brake Manning', nameFragments: ['rebekah', 'manning', 'brake', 'rm', 'rbm'] },
        'HD': { initials: 'HD', name: 'Himanie Dubois', nameFragments: ['himanie', 'dubois', 'hd'] },
        'SG': { initials: 'SG', name: 'Sangita Gohil', nameFragments: ['sangita', 'gohil', 'sg'] },
        'JD': { initials: 'JD', name: 'Jodie Daffin', nameFragments: ['jodie', 'daffin', 'jd'] },
        'EH': { initials: 'EH', name: 'Emily Howard', nameFragments: ['emily', 'howard', 'eh'] },
        'LG': { initials: 'LG', name: 'Liezel Gonzales', nameFragments: ['liezel', 'gonzales', 'lg'] },
      };
      return allMatters.filter(s => supervisedInitials.some(ini => {
        const info = feInfoMap[ini] || { initials: ini, name: ini, nameFragments: [ini.toLowerCase()] };
        return matchesFeeEarner(s, info);
      }));
    }
    return allMatters;
  })();

  const py = compact ? "py-1" : "py-2";
  const visible = showAll ? matters : matters.slice(0, DEFAULT_ROWS);

  useEffect(() => {
    if (!showWip || visible.length === 0) return;
    let cancelled = false;
    const mattersToFetch = visible.filter(m => {
      if (isResidentialMatter(m)) return false;
      const d = m.data || {};
      const ref = pick(m.matter_reference, d.matter_reference, m.provisional_id);
      if (!ref) return false;
      if (wipData[ref] && !wipData[ref].loading) return false;
      return true;
    });
    if (mattersToFetch.length === 0) return;
    const loadingState = {};
    mattersToFetch.forEach(m => {
      const ref = pick(m.matter_reference, m.data?.matter_reference, m.provisional_id);
      loadingState[ref] = { wip: 0, loading: true };
    });
    setWipData(prev => ({ ...prev, ...loadingState }));
    Promise.allSettled(
      mattersToFetch.map(async m => {
        const ref = pick(m.matter_reference, m.data?.matter_reference, m.provisional_id);
        try {
          const res = await axios.get(`${API}/lawbillity/billing-summary`, { params: { matter_ref: ref }, headers: headers() });
          const wip = parseFloat(res.data?.wip || res.data?.total_wip || 0);
          return { ref, wip };
        } catch {
          return { ref, wip: 0 };
        }
      })
    ).then(results => {
      if (cancelled) return;
      const updates = {};
      results.forEach(r => {
        if (r.status === "fulfilled") updates[r.value.ref] = { wip: r.value.wip, loading: false };
      });
      setWipData(prev => ({ ...prev, ...updates }));
    });
    return () => { cancelled = true; };
  }, [showWip, visible.length, matters.length, showAll]);

  return (
    <PanelShell title={isAssistant ? "Team Matters" : "My Matters"} icon={FileText} color="text-teal" loading={loading} error={error} testId="my-matters-panel">
      {allMatters.length === 0 && !loading ? <p className="text-xs text-txt-muted text-center py-4">No open matters</p> : (
        <>
          <div className="flex justify-between items-center mb-2">
            {canViewAllMatters && !isAssistant ? (
              <button onClick={() => setViewMode(v => v === "mine" ? "all" : "mine")}
                className="text-[10px] px-2 py-0.5 rounded bg-navy-hover text-txt-muted hover:bg-navy-hover transition-colors flex items-center gap-1"
                data-testid="my-matters-view-toggle">
                {viewMode === "mine" ? <ToggleLeft className="w-3 h-3" /> : <ToggleRight className="w-3 h-3 text-teal" />}
                {viewMode === "mine" ? "My matters" : "All matters"}
              </button>
            ) : <span />}
            <div className="flex items-center gap-1.5">
              <button onClick={() => setShowWip(w => !w)}
                className={`text-[10px] px-2 py-0.5 rounded transition-colors flex items-center gap-1 ${showWip ? "bg-status-amber text-white" : "bg-navy-hover text-txt-muted hover:bg-navy-hover"}`}
                data-testid="my-matters-wip-toggle">
                <PoundSterling className="w-3 h-3" />
                WIP
              </button>
              <button onClick={() => setCompact(c => !c)}
                className="text-[10px] px-2 py-0.5 rounded bg-navy-hover text-txt-muted hover:bg-navy-hover transition-colors"
                data-testid="my-matters-compact-toggle">{compact ? "Normal" : "Compact"}</button>
            </div>
          </div>
          {matters.length === 0 ? <p className="text-xs text-txt-muted text-center py-4">No matters assigned to you</p> : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead><tr className="text-[10px] text-txt-muted uppercase tracking-wider border-b border-navy-border">
                    <th className={`${py} pr-2 w-[90px]`}>Ref</th><th className={`${py} pr-2`}>Client</th><th className={`${py} pr-2`}>Description</th><th className={`${py} pr-2 w-[40px]`}>FE</th><th className={`${py} pr-2`}>Stage</th>{showWip && <th className={`${py} pr-2 w-[60px]`}>WIP</th>}<th className={`${py} w-[44px]`}>Age</th>
                  </tr></thead>
                  <tbody>
                    {visible.map(m => {
                      const d = m.data || {};
                      const created = m.created_at || m.createdAt;
                      const age = created ? differenceInDays(new Date(), parseISO(created)) : 0;
                      const mId = m.id || m._id;
                      const ref = pick(m.matter_reference, d.matter_reference, m.provisional_id);
                      const client = pick(m.client_display_name, d.client_name, d.Q1, m.primary_label);
                      const desc = pick(d.matter_title, d.description, d.Q3, d.Q2, d.Q10);
                      const fe = pick(d.fee_earner, d.Q6, d.assigned_to);
                      const stage = getMatterStageLabel(m);
                      const isResi = isResidentialMatter(m);
                      const wipInfo = ref && showWip && !isResi ? wipData[ref] : null;
                      return (
                        <TableRow key={mId} onClick={() => navigate(`/submission/${mId}`)}>
                          <td className={`${py} pr-2 font-mono text-teal text-[10px] w-[90px]`}>{ref || "—"}</td>
                          <td className={`${py} pr-2 text-white`}>{client}</td>
                          <td className={`${py} pr-2 max-w-[200px] truncate`}>{desc}</td>
                          <td className={`${py} pr-2 w-[40px]`} title={fe}>{initials(fe)}</td>
                          <td className={`${py} pr-2`}><StatusBadge status={stage.badge} label={stage.label} /></td>
                          {showWip && (
                            <td className={`${py} pr-2 w-[60px]`}>
                              {isResi ? <span className="text-[10px] text-txt-muted">—</span> : wipInfo?.loading ? (
                                <Loader2 className="w-3 h-3 animate-spin text-txt-muted" />
                              ) : wipInfo && wipInfo.wip > 0 ? (
                                <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-status-amber text-white">£{wipInfo.wip.toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                              ) : wipInfo ? (
                                <span className="text-[10px] text-txt-muted">£0</span>
                              ) : null}
                            </td>
                          )}
                          <td className={`${py} w-[44px]`}>{age}d</td>
                          <td className={`${py} pl-1`}><ChevronRight className="w-3 h-3 text-txt-muted" /></td>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {matters.length > DEFAULT_ROWS && (
                <button onClick={() => setShowAll(s => !s)}
                  className="text-[10px] text-teal hover:text-teal mt-2 w-full text-center transition-colors"
                  data-testid="my-matters-show-all">
                  {showAll ? "Show less" : `Show all (${matters.length})`}
                </button>
              )}
            </>
          )}
        </>
      )}
      <p className="text-[10px] text-txt-muted mt-2">{matters.length} matter{matters.length !== 1 ? "s" : ""}</p>
    </PanelShell>
  );
}

// ═══════════════════════════════════════════════
// 2.3 Deadlines & Stale Nudges Panel
// ═══════════════════════════════════════════════
export function DeadlinesNudgesPanel({ userEmail: userEmailProp }) {
  const navigate = useNavigate();
  const [deadlines, setDeadlines] = useState([]);
  const [stale, setStale] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const scopedHeaders = headers();
        const [dlRes, staleRes] = await Promise.all([
          axios.get(`${API}/bonsai/deadlines/tasks`, {
            params: { user_email: userEmailProp },
            headers: scopedHeaders,
          }).catch(() => ({ data: { tasks: [] } })),
          axios.get(`${API}/hub/stale-nudge/check`, {
            params: { user_email: userEmailProp },
            headers: scopedHeaders,
          }).catch(() => ({ data: { stale_matters: [] } })),
        ]);
        const tasks = dlRes.data?.tasks || dlRes.data || [];
        const now = new Date();
        const twoWeeks = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
        setDeadlines(tasks.filter(t => {
          if (!t.due_date) return false;
          const due = new Date(t.due_date);
          return due <= twoWeeks && t.status !== "completed";
        }).sort((a, b) => new Date(a.due_date) - new Date(b.due_date)));
        setStale(staleRes.data?.stale_matters || []);
      } catch {}
      setLoading(false);
    })();
  }, [userEmailProp]);

  return (
    <PanelShell title="Deadlines & Stale Matters" icon={Calendar} color="text-rose-400" loading={loading} testId="deadlines-nudges-panel">
      <h4 className="text-[10px] font-bold uppercase tracking-widest text-rose-400 mb-2">Deadlines — next 14 days</h4>
      {deadlines.length === 0 ? <p className="text-xs text-txt-muted py-2">No upcoming deadlines</p> : (
        <div className="space-y-1 mb-4">
          {deadlines.slice(0, 15).map((dl, i) => {
            const due = dl.due_date ? format(parseISO(dl.due_date), "dd MMM") : "—";
            const overdue = dl.due_date && new Date(dl.due_date) < new Date();
            const dlTarget = dl.submission_id || dl.matter_id || dl.id;
            return (
              <div key={dl.id || i}
                className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer hover:bg-navy-hover/70 ${overdue ? "bg-status-red-dim/20 border border-red-500/30" : "bg-navy-hover"}`}
                onClick={() => dlTarget && navigate(`/submission/${dlTarget}`)}>                
                <span className={`font-mono text-[10px] ${overdue ? "text-status-red" : "text-txt-muted"}`}>{due}</span>
                <span className="font-mono text-teal text-[10px]">{dl.matter_reference || ""}</span>
                <span className="text-white truncate flex-1">{dl.title || dl.description || ""}</span>
                <span className="text-[10px] text-txt-muted">{dl.category || ""}</span>
                <ChevronRight className="w-3 h-3 text-txt-muted shrink-0" />
              </div>
            );
          })}
        </div>
      )}
      <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-3 mb-2">Matters with no activity &gt;14 days</h4>
      {stale.length === 0 ? <p className="text-xs text-txt-muted py-2">No stale matters</p> : (
        <div className="space-y-1">
          {stale.slice(0, 10).map((s, i) => {
            const staleTarget = s.submission_id || s.matter_id || s.id;
            return (
              <div key={i}
                className="flex items-center gap-2 px-2 py-1.5 rounded bg-navy-hover text-xs cursor-pointer hover:bg-navy-hover/70"
                onClick={() => staleTarget && navigate(`/submission/${staleTarget}`)}>                
                <span className="font-mono text-teal text-[10px]">{s.matter_reference || ""}</span>
                <span className="text-white truncate flex-1">{s.client_name || ""}</span>
                <StatusBadge status="stale" label={`${s.days_inactive || 0}d inactive`} />
                <ChevronRight className="w-3 h-3 text-txt-muted shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </PanelShell>
  );
}

// ═══════════════════════════════════════════════
// 2.4 Residential Checklists & Completions Panel
// ═══════════════════════════════════════════════
export function ResiChecklistsPanel({ userEmail: userEmailProp }) {
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [loading, setLoading] = useState(true);

  const feInfo = getUserFeeEarner(userEmailProp);
  const supervisedInitials = SUPERVISED_FEE_EARNERS[userEmailProp] || (feInfo ? [feInfo.initials] : []);
  const userRole = getUserRole(userEmailProp);
  const showAll = userRole === ROLES.ADMIN;

  useEffect(() => {
    (async () => {
      try {
        const scopedHeaders = headers();
        const [clRes, riskRes] = await Promise.all([
          axios.get(`${API}/checklists/list`, {
            params: { user_email: userEmailProp },
            headers: scopedHeaders,
          }).catch(() => ({ data: [] })),
          axios.get(`${API}/completionsbook/at-risk`, {
            params: { days_ahead: 7, user_email: userEmailProp },
            headers: scopedHeaders,
          }).catch(() => ({ data: { entries: [] } })),
        ]);
        let cls = Array.isArray(clRes.data) ? clRes.data : clRes.data?.checklists || [];
        cls = cls.filter(c => c.status !== "approved" && c.status !== "completed");
        let risk = riskRes.data?.entries || (Array.isArray(riskRes.data) ? riskRes.data : []);

        if (!showAll && supervisedInitials.length > 0) {
          const matchesUser = (item) => {
            const fe = (item.fee_earner || "").toUpperCase();
            const ref = (item.matter_reference || item.data?.matterref || "").toUpperCase();
            return supervisedInitials.some(ini => fe.includes(ini) || ref.includes(`.${ini}.`));
          };
          cls = cls.filter(matchesUser);
          risk = risk.filter(matchesUser);
        }

        setChecklists(cls);
        setAtRisk(risk);
      } catch {}
      setLoading(false);
    })();
  }, [userEmailProp]);

  return (
    <PanelShell title="Checklists & Completions at Risk" icon={ClipboardCheck} color="text-purple-400" loading={loading} testId="resi-checklists-panel">
      <h4 className="text-[10px] font-bold uppercase tracking-widest text-purple-400 mb-2">Checklists not approved</h4>
      {checklists.length === 0 ? <p className="text-xs text-txt-muted py-2">All checklists approved</p> : (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-left">
            <thead><tr className="text-[10px] text-txt-muted uppercase border-b border-navy-border">
              <th className="py-1.5 pr-2">Type</th><th className="py-1.5 pr-2">Matter</th><th className="py-1.5 pr-2">Status</th><th className="py-1.5">FE</th>
            </tr></thead>
            <tbody>
              {checklists.slice(0, 20).map(cl => (
                <TableRow key={cl.id} onClick={() => navigate(`/submission/${cl.submission_id || cl.id}`)}>
                  <td className="py-1.5 pr-2 capitalize">{(cl.type || "").replace(/_/g, " ")}</td>
                  <td className="py-1.5 pr-2 font-mono text-teal text-[10px]">{cl.matter_reference || ""}</td>
                  <td className="py-1.5 pr-2"><StatusBadge status="pending" label={cl.status} /></td>
                  <td className="py-1.5">{cl.fee_earner || ""}</td>
                  <td className="py-1.5 pl-1"><ChevronRight className="w-3 h-3 text-txt-muted" /></td>
                </TableRow>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h4 className="text-[10px] font-bold uppercase tracking-widest text-status-amber mt-3 mb-2">Completions at risk (next 7 working days)</h4>
      {atRisk.length === 0 ? <p className="text-xs text-txt-muted py-2">No at-risk completions</p> : (
        <div className="space-y-1">
          {atRisk.slice(0, 15).map((e, i) => {
            const flags = [];
            if (e.data?.redemption_statement_received === "no") flags.push("Redemption");
            if (e.data?.cot_submitted === "no") flags.push("COT");
            if (e.data?.completion_statement_prepared !== "yes") flags.push("Statement");
            if (e.data?.client_funds_requested === "no") flags.push("Funds");
            const riskTarget = e.id;
            return (
              <div key={e.id || i}
                className="flex items-center gap-2 px-2 py-1.5 rounded bg-status-amber-dim/15 border border-orange-500/20 text-xs cursor-pointer hover:bg-status-amber-dim/25"
                onClick={() => riskTarget && navigate(`/completions-book/${riskTarget}`)}>                
                <span className="font-mono text-teal text-[10px]">{e.data?.matterref || ""}</span>
                <span className="text-white truncate flex-1">{e.data?.client_name || ""}</span>
                <span className="text-[10px] text-status-amber">{e.data?.completion_date || ""}</span>
                {flags.length > 0 && <span className="text-[10px] text-status-red">{flags.join(", ")}</span>}
                <ChevronRight className="w-3 h-3 text-txt-muted shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </PanelShell>
  );
}

// ═══════════════════════════════════════════════
// 2.5 Post-Completion Tasks Panel
// ═══════════════════════════════════════════════
export function PostCompletionTasksPanel({ userEmail: userEmailProp }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const feInfo = getUserFeeEarner(userEmailProp);
  const supervisedInitials = SUPERVISED_FEE_EARNERS[userEmailProp] || (feInfo ? [feInfo.initials] : []);
  const userRole = getUserRole(userEmailProp);
  const showAllFlag = userRole === ROLES.ADMIN;

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/checklists/list`, {
          params: { checklist_type: "post_completion", user_email: userEmailProp },
          headers: headers(),
        });
        let cls = Array.isArray(res.data) ? res.data : res.data?.checklists || [];
        cls = cls.filter(c => c.status !== "completed" && c.status !== "approved");

        if (!showAllFlag && supervisedInitials.length > 0) {
          cls = cls.filter(t => {
            const fe = (t.fee_earner || "").toUpperCase();
            const ref = (t.matter_reference || "").toUpperCase();
            return supervisedInitials.some(ini => fe.includes(ini) || ref.includes(`.${ini}.`));
          });
        }

        setTasks(cls);
      } catch {}
      setLoading(false);
    })();
  }, [userEmailProp]);

  return (
    <PanelShell title="Post-Completion Tasks" icon={CheckCircle} color="text-teal" loading={loading} testId="post-completion-panel">
      {tasks.length === 0 ? <p className="text-xs text-txt-muted text-center py-4">No outstanding post-completion tasks</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="text-[10px] text-txt-muted uppercase border-b border-navy-border">
              <th className="py-1.5 pr-2">Matter</th><th className="py-1.5 pr-2">Client</th><th className="py-1.5 pr-2">Status</th><th className="py-1.5">FE</th>
            </tr></thead>
            <tbody>
              {tasks.slice(0, 30).map(t => (
                <TableRow key={t.id} onClick={() => navigate(`/submission/${t.submission_id || t.id}`)}>
                  <td className="py-1.5 pr-2 font-mono text-teal text-[10px]">{t.matter_reference || ""}</td>
                  <td className="py-1.5 pr-2 text-white">{t.client_name || ""}</td>
                  <td className="py-1.5 pr-2"><StatusBadge status="pending" label={t.status} /></td>
                  <td className="py-1.5">{t.fee_earner || ""}</td>
                  <td className="py-1.5 pl-1"><ChevronRight className="w-3 h-3 text-txt-muted" /></td>
                </TableRow>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] text-txt-muted mt-2">{tasks.length} outstanding</p>
    </PanelShell>
  );
}

// ═══════════════════════════════════════════════
// 2.6 Post-Intake Queue Panel
// ═══════════════════════════════════════════════
export function PostIntakeQueuePanel({ userEmail: userEmailProp }) {
  const navigate = useNavigate();
  const [unclaimed, setUnclaimed] = useState([]);
  const [staleClai, setStaleClai] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("unclaimed");

  const userRole = getUserRole(userEmailProp);
  const feInfo = getUserFeeEarner(userEmailProp);
  const supervisedInitials = SUPERVISED_FEE_EARNERS[userEmailProp] || (feInfo ? [feInfo.initials] : []);
  const isPostScanning = userRole === ROLES.POST_SCANNING;

  useEffect(() => {
    (async () => {
      try {
        const scopedHeaders = headers();
        const [unRes, clRes] = await Promise.all([
          axios.get(`${API}/postintake/queue`, {
            params: { status: "pending", user_email: userEmailProp },
            headers: scopedHeaders,
          }).catch(() => ({ data: [] })),
          axios.get(`${API}/postintake/queue`, {
            params: { status: "claimed", user_email: userEmailProp },
            headers: scopedHeaders,
          }).catch(() => ({ data: [] })),
        ]);
        let unArr = Array.isArray(unRes.data) ? unRes.data : unRes.data?.items || [];
        let clArr = Array.isArray(clRes.data) ? clRes.data : clRes.data?.items || [];

        if (!isPostScanning && supervisedInitials.length > 0) {
          const matchesUser = (item) => {
            const fe = (item.fee_earner || "").toUpperCase();
            const ref = (item.matter_reference || "").toUpperCase();
            return supervisedInitials.some(ini => fe.includes(ini) || ref.includes(`.${ini}.`));
          };
          unArr = unArr.filter(matchesUser);
          clArr = clArr.filter(matchesUser);
        }

        setUnclaimed(unArr);
        const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);
        setStaleClai(clArr.filter(c => c.claimed_at && new Date(c.claimed_at) < threeDaysAgo));
      } catch {}
      setLoading(false);
    })();
  }, [userEmailProp]);

  const items = tab === "unclaimed" ? unclaimed : staleClai;

  return (
    <PanelShell title="Post-Intake Queue" icon={Inbox} color="text-blue-400" loading={loading} testId="post-intake-panel">
      <div className="flex gap-1.5 mb-3">
        <button onClick={() => setTab("unclaimed")}
          className={`text-[10px] px-2 py-1 rounded ${tab === "unclaimed" ? "bg-blue-700 text-white" : "bg-navy-hover text-txt-muted"}`}
          data-testid="post-intake-tab-unclaimed">Unclaimed ({unclaimed.length})</button>
        <button onClick={() => setTab("stale")}
          className={`text-[10px] px-2 py-1 rounded ${tab === "stale" ? "bg-status-amber text-white" : "bg-navy-hover text-txt-muted"}`}
          data-testid="post-intake-tab-stale">Claimed 3+ days ({staleClai.length})</button>
      </div>
      {items.length === 0 ? <p className="text-xs text-txt-muted text-center py-4">{tab === "unclaimed" ? "No unclaimed items" : "No stale claimed items"}</p> : (
        <div className="space-y-1">
          {items.slice(0, 30).map((it, i) => {
            const created = it.created_at ? format(parseISO(it.created_at), "dd MMM HH:mm") : "";
            const intakeTarget = it.submission_id || it.id;
            return (
              <div key={it.id || i}
                className="flex items-center gap-2 px-2 py-1.5 rounded bg-navy-hover text-xs cursor-pointer hover:bg-navy-hover/70"
                onClick={() => intakeTarget && navigate(`/submission/${intakeTarget}`)}>                
                <span className="text-[10px] text-txt-muted">{created}</span>
                <span className="text-white truncate flex-1">{it.client_name || it.description || "—"}</span>
                <span className="text-[10px] text-txt-muted">{it.fee_earner || ""}</span>
                {tab === "stale" && <StatusBadge status="overdue" label="3+ days" />}
                <ChevronRight className="w-3 h-3 text-txt-muted shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </PanelShell>
  );
}

// ═══════════════════════════════════════════════
// 2.7 File-Closure & Risk Panel
// ═══════════════════════════════════════════════
export function FileClosureRiskPanel({ userEmail: userEmailProp }) {
  const navigate = useNavigate();
  const [closures, setClosures] = useState([]);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);

  const feInfo = getUserFeeEarner(userEmailProp);
  const supervisedInitials = SUPERVISED_FEE_EARNERS[userEmailProp] || (feInfo ? [feInfo.initials] : []);
  const userRole = getUserRole(userEmailProp);
  const showAllFlag = userRole === ROLES.ACCOUNTS || userRole === ROLES.ADMIN;

  useEffect(() => {
    (async () => {
      try {
        const scopedHeaders = headers();
        const [clRes, riskRes] = await Promise.all([
          axios.get(`${API}/submissions`, {
            params: { form_type: "file_closure", limit: 50, user_email: userEmailProp },
            headers: scopedHeaders,
          }).catch(() => ({ data: { submissions: [] } })),
          axios.get(`${API}/submissions`, {
            params: { form_type: "risk_assessment", limit: 50, user_email: userEmailProp },
            headers: scopedHeaders,
          }).catch(() => ({ data: { submissions: [] } })),
        ]);
        let clSubs = (clRes.data?.submissions || []).filter(s => s.status === "submitted" || s.status === "pending");
        let riskSubs = (riskRes.data?.submissions || []).filter(s => s.status === "submitted" || s.status === "pending");

        if (!showAllFlag && supervisedInitials.length > 0) {
          const matchesUser = (sub) => {
            const d = sub.data || {};
            const fe = pick(d.fee_earner, d.Q6, d.assigned_to).toUpperCase();
            const ref = (sub.matter_reference || "").toUpperCase();
            return supervisedInitials.some(ini => fe.includes(ini) || ref.includes(`.${ini}.`));
          };
          clSubs = clSubs.filter(matchesUser);
          riskSubs = riskSubs.filter(matchesUser);
        }

        setClosures(clSubs);
        setRisks(riskSubs);
      } catch {}
      setLoading(false);
    })();
  }, [userEmailProp]);

  return (
    <PanelShell title="File Closure & Risk" icon={Scale} color="text-rose-400" loading={loading} testId="file-closure-risk-panel">
      <h4 className="text-[10px] font-bold uppercase tracking-widest text-rose-400 mb-2">Pending file closures</h4>
      {closures.length === 0 ? <p className="text-xs text-txt-muted py-2">No pending closures</p> : (
        <div className="space-y-1 mb-4">
          {closures.map((c, i) => (
            <div key={c.id || i}
              className="flex items-center gap-2 px-2 py-1.5 rounded bg-navy-hover text-xs cursor-pointer hover:bg-navy-hover/70"
              onClick={() => navigate(`/file-closure?matter_reference=${encodeURIComponent(c.matter_reference || "")}`)}>              
              <span className="font-mono text-teal text-[10px]">{c.matter_reference || ""}</span>
              <span className="text-white truncate flex-1">{pick(c.client_display_name, c.data?.client_name, c.data?.Q1, c.primary_label)}</span>
              <span className="text-[10px] text-txt-muted">{pick(c.data?.fee_earner, c.data?.Q6, c.data?.assigned_to)}</span>
              <ChevronRight className="w-3 h-3 text-txt-muted shrink-0" />
            </div>
          ))}
        </div>
      )}
      <h4 className="text-[10px] font-bold uppercase tracking-widest text-status-amber mt-3 mb-2">Risk assessments awaiting review</h4>
      {risks.length === 0 ? <p className="text-xs text-txt-muted py-2">No pending risk assessments</p> : (
        <div className="space-y-1">
          {risks.map((r, i) => (
            <div key={r.id || i}
              className="flex items-center gap-2 px-2 py-1.5 rounded bg-navy-hover text-xs cursor-pointer hover:bg-navy-hover/70"
              onClick={() => navigate(`/submission/${r.id}`)}>              
              <span className="font-mono text-teal text-[10px]">{r.matter_reference || ""}</span>
              <span className="text-white truncate flex-1">{pick(r.client_display_name, r.data?.client_name, r.data?.Q1, r.primary_label)}</span>
              <span className="text-[10px] text-txt-muted">{pick(r.data?.fee_earner, r.data?.Q6, r.data?.assigned_to)}</span>
              <ChevronRight className="w-3 h-3 text-txt-muted shrink-0" />
            </div>
          ))}
        </div>
      )}
    </PanelShell>
  );
}

// ═══════════════════════════════════════════════
// 2.8 Accounts Today Panel (ACCOUNTS + DIRECTOR roles)
//     May sees it as her home panel; Directors see it via
//     EMAIL_PANEL_OVERRIDES so they can watch cash movements,
//     approve payment requests, and eyeball credit control
//     without having to log in as May.
// ═══════════════════════════════════════════════

const ACCOUNTS_REQUEST_TYPES = [
  { form_type: "invoice_request",       label: "Invoice Request",        icon: Receipt,         color: "text-blue-400" },
  { form_type: "payment_request",       label: "Payment Request",        icon: CreditCard,       color: "text-teal" },
  { form_type: "accounts_instruction",  label: "Completion Funds",       icon: Banknote,         color: "text-teal" },
  { form_type: "completion_statement",  label: "Completion Statement",   icon: FileText,         color: "text-purple-400" },
  { form_type: "third_party_invoices",  label: "Third-Party Invoice",    icon: FileSpreadsheet,  color: "text-status-amber" },
];

export function AccountsTodayPanel({ userEmail: userEmailProp }) {
  // Trust-context: prefer the email passed by the parent (already resolved
  // from the canonical user object on Dashboard.jsx). Fall back through
  // sessionStorage / localStorage only when the parent didn't pass one —
  // the storage path is racy on first paint after MS-SSO callback.
  const resolvedEmail = (
    userEmailProp ||
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    ""
  ).toLowerCase().trim();
  // While email hasn't hydrated yet, render nothing (parent shouldn't be
  // mounting the panel without a resolved role anyway). This is a single
  // render cycle; once email lands, the parent re-renders and we proceed.
  if (!resolvedEmail) return null;
  const resolvedRole = getUserRole(resolvedEmail);
  // Accounts sees it always. Directors see it too, so they can approve
  // payment requests and monitor credit control from their own home page.
  if (resolvedRole !== ROLES.ACCOUNTS && resolvedRole !== ROLES.DIRECTOR) return null;
  return <AccountsTodayPanelInner />;
}

const MOA_PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "card", label: "Card" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other" },
];

function AccountsTodayPanelInner() {
  const [ccData, setCcData] = useState(null);
  const [ccLoadError, setCcLoadError] = useState("");
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingCc, setLoadingCc] = useState(true);
  const [loadingReq, setLoadingReq] = useState(true);
  const [activeTab, setActiveTab] = useState("cc");
  const navigate = useNavigate();

  // Money on Account form state
  const [showMoaForm, setShowMoaForm] = useState(false);
  const [moaSubmitting, setMoaSubmitting] = useState(false);
  const [moaForm, setMoaForm] = useState({
    matter_reference: "",
    amount: "",
    date_received: new Date().toISOString().split("T")[0],
    payment_method: "bank_transfer",
    notes: "",
  });

  // CSV upload state
  const [importLock, setImportLock] = useState(null); // {locked, completed_at, completed_by, ...}
  const [recentImports, setRecentImports] = useState([]);
  // BM-H22 (16 Jul 2026): signpost the one-time trust import. May Ann
  // reported Trust Accounts had no records and she "could not see any
  // option to do an import" — the importer lives behind the MoA sub-tab
  // (default tab is CC), so it was invisible on first landing. We fetch
  // the lock status on mount and, while the import is still available,
  // show a dismissible banner pointing straight at it.
  const [importSignpostDismissed, setImportSignpostDismissed] = useState(false);

  // BM-H627 (26 Sep 2026): the manual LawBillity WIP sync is May's alone
  // ("100% absolutely gated to May only"). The server decides who that is
  // (/user-role can_sync_wip) and checks again when the sync is run.
  const [canSyncWip, setCanSyncWip] = useState(false);
  const [syncingWip, setSyncingWip] = useState(false);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/user-role`, { headers: headers() })
      .then((res) => { if (!cancelled) setCanSyncWip(res.data?.can_sync_wip === true); })
      .catch(() => { if (!cancelled) setCanSyncWip(false); });
    return () => { cancelled = true; };
  }, []);
  const syncWip = async () => {
    setSyncingWip(true);
    try {
      await axios.post(`${API}/lawbillity/sync-wip`, {}, { headers: headers() });
      toast.success("WIP sync started");
    } catch (err) {
      toast.error(errorMessage(err, "WIP sync failed"));
    }
    setSyncingWip(false);
  };

  const fetchRecentImports = async () => {
    try {
      const res = await axios.get(`${API}/money-on-account/recent`, {
        params: { limit: 10, source: "opening_balance" },
        headers: headers(),
      });
      setRecentImports(res.data?.entries || []);
    } catch { }
  };

  const fetchImportLock = async () => {
    try {
      const res = await axios.get(`${API}/money-on-account/import-lock-status`, {
        headers: headers(),
      });
      setImportLock(res.data || null);
    } catch { }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/credit-control/accounts-today`, { headers: headers() });
        setCcData(res.data);
        setCcLoadError("");
      } catch (err) {
        const status = err?.response?.status;
        setCcLoadError(
          status === 401 || status === 403
            ? "Session expired"
            : errorMessage(err, "Could not load CC data")
        );
      }
      finally { setLoadingCc(false); }
    })();
  }, []);

  // BM-H22: fetch the trust-import lock + recent-imports on mount (not just
  // when the MoA tab is opened) so the signpost banner can decide whether
  // the one-time import still needs doing before the user clicks anything.
  useEffect(() => {
    fetchImportLock();
    fetchRecentImports();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const results = await Promise.allSettled(
          ACCOUNTS_REQUEST_TYPES.map(({ form_type }) =>
            axios.get(`${API}/submissions`, {
              params: { form_type, status: "pending", limit: 50 },
              headers: headers(),
            })
          )
        );
        const all = [];
        results.forEach((r, i) => {
          if (r.status === "fulfilled") {
            const items = r.value.data?.submissions || r.value.data || [];
            items.forEach(sub => {
              all.push({ ...sub, _form_type: ACCOUNTS_REQUEST_TYPES[i].form_type, _label: ACCOUNTS_REQUEST_TYPES[i].label, _color: ACCOUNTS_REQUEST_TYPES[i].color, _Icon: ACCOUNTS_REQUEST_TYPES[i].icon });
            });
          }
        });
        all.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
        setPendingRequests(all);
      } catch { }
      finally { setLoadingReq(false); }
    })();
  }, []);

  const totalPending = pendingRequests.length;

  const handleMoaSubmit = async () => {
    if (!moaForm.matter_reference.trim()) { toast.error("Matter reference is required"); return; }
    if (!moaForm.amount || parseFloat(moaForm.amount) <= 0) { toast.error("Amount must be greater than zero"); return; }
    setMoaSubmitting(true);
    try {
      await axios.post(`${API}/money-on-account`, {
        ...moaForm,
        amount: parseFloat(moaForm.amount),
        recorded_by: userEmail(),
      }, { headers: headers() });
      toast.success("Money on account recorded");
      setMoaForm({ matter_reference: "", amount: "", date_received: new Date().toISOString().split("T")[0], payment_method: "bank_transfer", notes: "" });
      setShowMoaForm(false);
    } catch (err) {
      // Trust-context: surface the BE reason (401/403/etc) verbatim so a
      // permissions regression cannot masquerade as a generic failure.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        toast.error("Session expired — please sign in again");
      } else if (status === 403) {
        toast.error(detail || "Not authorised to record money on account");
      } else {
        toast.error(errorMessage(err, "Failed to record money on account"));
      }
    } finally {
      setMoaSubmitting(false);
    }
  };

  return (
    <PanelShell title="Accounts Today" icon={Clock} testId="accounts-today-panel" loading={loadingCc && loadingReq}>
      {/* BM-H22: one-time trust-import signpost. Shows only while the import
          is still available (not locked) and nothing has been imported yet,
          so nobody has to hunt for it behind the MoA sub-tab. Dismissible. */}
      {!importLock?.locked && recentImports.length === 0 && !importSignpostDismissed && (
        <div
          className="mb-3 flex items-start gap-2 rounded-lg border border-teal/40 bg-teal/10 px-3 py-2"
          data-testid="moa-import-signpost"
        >
          <Upload className="w-3.5 h-3.5 text-teal mt-0.5 shrink-0" />
          <div className="flex-1 text-[11px] leading-snug text-txt-muted">
            <span className="font-semibold text-white">Client ledger opening balances not loaded yet.</span>{" "}
            Load them once from LawBillity on the Client Account page.
            <button
              onClick={() => navigate("/client-account?tab=opening")}
              className="ml-1 font-semibold text-teal underline underline-offset-2 hover:text-white transition-colors"
              data-testid="moa-import-signpost-cta"
            >
              Open
            </button>
          </div>
          <button
            onClick={() => setImportSignpostDismissed(true)}
            className="text-txt-muted hover:text-white text-[11px] leading-none"
            aria-label="Dismiss"
            data-testid="moa-import-signpost-dismiss"
          >
            ×
          </button>
        </div>
      )}
      <div className="flex gap-1.5 mb-3">
        <button onClick={() => setActiveTab("cc")} className={`text-[10px] px-2.5 py-1 rounded transition-colors ${activeTab === "cc" ? "bg-teal text-white" : "bg-navy-hover text-txt-muted hover:bg-navy-hover"}`} data-testid="accounts-tab-cc">
          CC Actions {ccData && ccData.actions_due_count > 0 && <span className="ml-1.5 px-1 py-0.5 rounded bg-status-amber/30 text-orange-300 text-[9px] font-bold">{ccData.actions_due_count}</span>}
        </button>
        <button onClick={() => setActiveTab("requests")} className={`text-[10px] px-2.5 py-1 rounded transition-colors ${activeTab === "requests" ? "bg-blue-700 text-white" : "bg-navy-hover text-txt-muted hover:bg-navy-hover"}`} data-testid="accounts-tab-requests">
          Pending Requests {totalPending > 0 && <span className="ml-1.5 px-1 py-0.5 rounded bg-blue-500/30 text-blue-300 text-[9px] font-bold">{totalPending}</span>}
        </button>
        <button onClick={() => { setShowMoaForm(!showMoaForm); setActiveTab("moa"); fetchRecentImports(); fetchImportLock(); }} className={`text-[10px] px-2.5 py-1 rounded transition-colors flex items-center gap-1 ${activeTab === "moa" ? "bg-teal text-white" : "bg-navy-hover text-txt-muted hover:bg-navy-hover"}`} data-testid="accounts-tab-moa">
          <Plus className="w-3 h-3" /> Money on A/C
        </button>
        {canSyncWip && (
          <button onClick={syncWip} disabled={syncingWip}
            className="ml-auto text-[10px] px-2.5 py-1 rounded bg-navy-hover text-txt-muted hover:bg-navy-hover flex items-center gap-1 disabled:opacity-50"
            data-testid="accounts-sync-wip-btn">
            {syncingWip ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Sync WIP
          </button>
        )}
      </div>
      {activeTab === "cc" && (
        loadingCc ? <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-teal" /></div> :
        !ccData ? <p className="text-xs text-status-red text-center py-4" data-testid="accounts-today-cc-error">{ccLoadError || "Could not load CC data"}</p> : (
          <div className="space-y-1">
            {ccData.actions_due?.slice(0, 5).map(a => (
              <div key={a.id} onClick={() => navigate(`/credit-control/${a.id}`)} className="flex items-center justify-between bg-white/5 rounded px-2.5 py-1.5 text-xs cursor-pointer hover:bg-white/10">
                <span className="font-mono text-gray-400 mr-1.5">{a.matter_ref}</span>
                <span className="text-white truncate flex-1">{a.client_name}</span>
                <span className="text-status-amber font-bold ml-2">{a.days_overdue}d</span>
              </div>
            ))}
          </div>
        )
      )}
      {activeTab === "requests" && (
        loadingReq ? <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-teal" /></div> :
        pendingRequests.length === 0 ? <p className="text-xs text-txt-muted text-center py-6">No pending requests — all clear ✓</p> : (
          <div className="space-y-1">
            {pendingRequests.map((req, i) => {
              const d = req.data || {};
              const client = pick(req.client_display_name, d.client_name, d.Q1, req.primary_label, "—");
              const ref = pick(req.matter_reference, d.matter_reference, req.provisional_id);
              const Icon = req._Icon;
              return (
                <div key={req.id || i} onClick={() => navigate(`/submission/${req.id}`)} className="flex items-center gap-2 px-2.5 py-2 rounded bg-navy-hover text-xs cursor-pointer hover:bg-navy-hover">
                  {Icon && <Icon className={`w-3.5 h-3.5 shrink-0 ${req._color}`} />}
                  <div className="min-w-0 flex-1">
                    <span className={`text-[10px] font-semibold ${req._color}`}>{req._label}</span>
                    {ref && <span className="font-mono text-teal text-[10px] ml-1.5">{ref}</span>}
                    <span className="text-white truncate block">{client}</span>
                  </div>
                  <ChevronRight className="w-3 h-3 text-txt-muted shrink-0" />
                </div>
              );
            })}
          </div>
        )
      )}
      {activeTab === "moa" && (
        <div className="space-y-3" data-testid="moa-form">
          {/* BM-H4699: opening balances load on the Client Account page now,
              with a preview first; the July importer is retired. */}
          <div className="border border-dashed border-navy-border rounded-lg p-3" data-testid="moa-opening-link">
            <label className="text-[10px] text-txt-muted uppercase tracking-wider block mb-2">LawBillity opening balances</label>
            {importLock?.locked ? (
              <div className="p-2.5 rounded bg-navy-hover/60 border border-navy-border text-[10px]" data-testid="moa-import-locked">
                <div className="flex items-center gap-1.5 text-teal font-semibold">
                  <Lock className="w-3 h-3" />
                  Loaded{importLock.completed_at ? ` ${new Date(importLock.completed_at).toLocaleDateString("en-GB")}` : ""}
                  {importLock.completed_by ? ` by ${importLock.completed_by}` : ""}
                </div>
              </div>
            ) : (
              <button
                onClick={() => navigate("/client-account?tab=opening")}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded bg-navy-hover text-txt-muted text-xs border border-navy-border hover:text-white"
                data-testid="moa-opening-open"
              >
                <Upload className="w-3 h-3" />
                Preview and load on the Client Account page
              </button>
            )}
            {recentImports.length > 0 && (
              <div className="mt-2">
                <span className="text-[9px] text-txt-muted uppercase tracking-wider">Opening balances</span>
                <div className="mt-1 space-y-0.5">
                  {recentImports.slice(0, 5).map((entry, i) => (
                    <div key={i} className="flex items-center justify-between text-[10px] px-1.5 py-1 rounded bg-navy-hover/50">
                      <span className="font-mono text-teal truncate mr-2">{entry.matter_reference}</span>
                      <span className="text-white font-medium shrink-0">£{entry.amount?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-2">
            <div className="flex-1 border-t border-navy-border" />
            <span className="text-[9px] text-txt-muted uppercase">or record manually</span>
            <div className="flex-1 border-t border-navy-border" />
          </div>

          {/* Manual Entry Form */}
          <div>
            <label className="text-[10px] text-txt-muted uppercase tracking-wider block mb-1">Matter Reference</label>
            <input
              type="text"
              value={moaForm.matter_reference}
              onChange={(e) => setMoaForm({ ...moaForm, matter_reference: e.target.value })}
              placeholder="BL.RM.001.0001"
              className="w-full px-2.5 py-1.5 rounded bg-navy-hover border border-navy-border text-white text-xs placeholder-[#7a8ba8] focus:outline-none focus:border-teal"
              data-testid="moa-matter-ref"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-txt-muted uppercase tracking-wider block mb-1">Amount (£)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={moaForm.amount}
                onChange={(e) => setMoaForm({ ...moaForm, amount: e.target.value })}
                placeholder="5000.00"
                className="w-full px-2.5 py-1.5 rounded bg-navy-hover border border-navy-border text-white text-xs placeholder-[#7a8ba8] focus:outline-none focus:border-teal"
                data-testid="moa-amount"
              />
            </div>
            <div>
              <label className="text-[10px] text-txt-muted uppercase tracking-wider block mb-1">Date Received</label>
              <input
                type="date"
                value={moaForm.date_received}
                onChange={(e) => setMoaForm({ ...moaForm, date_received: e.target.value })}
                className="w-full px-2.5 py-1.5 rounded bg-navy-hover border border-navy-border text-white text-xs focus:outline-none focus:border-teal"
                data-testid="moa-date"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-txt-muted uppercase tracking-wider block mb-1">Payment Method</label>
            <select
              value={moaForm.payment_method}
              onChange={(e) => setMoaForm({ ...moaForm, payment_method: e.target.value })}
              className="w-full px-2.5 py-1.5 rounded bg-navy-hover border border-navy-border text-white text-xs focus:outline-none focus:border-teal"
              data-testid="moa-payment-method"
            >
              {MOA_PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-txt-muted uppercase tracking-wider block mb-1">Notes (optional)</label>
            <textarea
              value={moaForm.notes}
              onChange={(e) => setMoaForm({ ...moaForm, notes: e.target.value })}
              placeholder="Initial payment on account"
              rows={2}
              className="w-full px-2.5 py-1.5 rounded bg-navy-hover border border-navy-border text-white text-xs placeholder-[#7a8ba8] focus:outline-none focus:border-teal resize-none"
              data-testid="moa-notes"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleMoaSubmit}
              disabled={moaSubmitting}
              className="flex-1 px-3 py-1.5 rounded bg-teal hover:bg-teal-hover text-white text-xs font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
              data-testid="moa-submit"
            >
              {moaSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <PoundSterling className="w-3 h-3" />}
              Record Payment
            </button>
            <button
              onClick={() => { setShowMoaForm(false); setActiveTab("cc"); }}
              className="px-3 py-1.5 rounded bg-navy-hover hover:bg-navy-hover text-txt-muted text-xs transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </PanelShell>
  );
}
